import { useState, useEffect } from 'react';
import { useDarkMode } from '@hooks/useDarkMode';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from '@contexts/AuthContext';

import Navbar        from '@components/Navbar';
import Landing       from '@pages/Landing';
import Login         from '@pages/Login';
import Signup        from '@pages/Signup';
import Onboarding    from '@pages/Onboarding';
import Dashboard     from '@pages/Dashboard';
import PromptSandbox from '@features/ai-prompting/pages/PromptSandbox';
import Skills        from '@pages/Skills';
import Settings      from '@pages/Settings';
import Billing       from '@pages/Billing';
import Feedback      from '@pages/Feedback';
import Privacy       from '@pages/Privacy';
import Terms         from '@pages/Terms';
import NotFound      from '@pages/NotFound';
import Contact       from '@pages/Contact';
import About         from '@pages/About';
import Faq           from '@pages/Faq';
import VoiceRoom     from '@pages/VoiceRoom';
import SalesNegotiationLanding from '@features/sales-negotiation/pages/SalesNegotiationLanding';
import PromptLanding           from '@features/ai-prompting/pages/PromptLanding';

import CopywritingLanding from '@features/copywriting/pages/CopywritingLanding';
import HeadlineDuel      from '@features/copywriting/pages/HeadlineDuel';
import LivingAd          from '@features/copywriting/pages/LivingAd';
import SwipeFile         from '@features/copywriting/pages/SwipeFile';
import CopywritingBrief  from '@features/copywriting/pages/CopywritingBrief';
import ABArena           from '@features/copywriting/pages/ABArena';

import StorytellingLanding from '@features/storytelling/pages/StorytellingLanding';
import OriginCut         from '@features/storytelling/pages/OriginCut';
import TurningPoint      from '@features/storytelling/pages/TurningPoint';
import DataStory         from '@features/storytelling/pages/DataStory';
import PitchArc          from '@features/storytelling/pages/PitchArc';
import Callback          from '@features/storytelling/pages/Callback';

import AppLayout     from '@components/AppLayout';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
};

const OnboardingRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/signup" replace />;
  return children;
};

/* ─── Cookie / GDPR banner ── */
const COOKIE_KEY = 'ca_cookie_consent';
// One geometry on every route. These are the values the landing page used to
// force via index.css (html.landing-terminal override); the other routes had
// 16/10/8, which is why the banner looked different on /login.
const BANNER_RADIUS = 4;
const BUTTON_RADIUS = 3;

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const isDark = useDarkMode();

  useEffect(() => {
    if (!localStorage.getItem(COOKIE_KEY)) {
      const t = setTimeout(() => setVisible(true), 900);
      return () => clearTimeout(t);
    }
  }, []);

  const accept  = () => { localStorage.setItem(COOKIE_KEY, 'accepted');  setVisible(false); };
  const decline = () => { localStorage.setItem(COOKIE_KEY, 'declined'); setVisible(false); };

  if (!visible) return null;

  const bg     = isDark ? '#1c1917' : '#ffffff';
  const border = isDark ? '#2d2825' : '#e8e4df';
  const head   = isDark ? '#fafaf9' : '#1c1917';
  const muted  = isDark ? '#a8a29e' : '#78716c';

  return (
    <>
      <style>{`
        @keyframes cookie-in {
          from { opacity: 0; transform: translateX(-50%) translateY(14px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
      <div role="dialog" aria-label="Cookie consent" style={{
        position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)',
        zIndex: 99999, width: 'calc(100% - 32px)', maxWidth: 560,
        background: bg, border: `1px solid ${border}`, borderRadius: BANNER_RADIUS, padding: '14px 18px',
        boxShadow: isDark ? '0 8px 40px rgba(0,0,0,0.5)' : '0 8px 40px rgba(0,0,0,0.10)',
        display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
        animation: 'cookie-in 0.35s cubic-bezier(0.34,1.56,0.64,1) both',
      }}>
        <div style={{ width: 34, height: 34, borderRadius: BANNER_RADIUS, flexShrink: 0, background: isDark ? '#2d1f0e' : '#fff7ed', border: `1px solid ${isDark ? '#5c3d1e' : '#fed7aa'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>🍪</div>
        <div style={{ flex: 1, minWidth: 180 }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: head, marginBottom: 2 }}>We use cookies</p>
          <p style={{ fontSize: 12, color: muted, lineHeight: 1.5 }}>
            Analytics only (PostHog) — no ads, no selling data.{' '}
            <a href="/privacy" style={{ color: '#ea580c', fontWeight: 600, textDecoration: 'none' }}>Privacy Policy</a>
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button onClick={decline} style={{ padding: '6px 14px', borderRadius: BUTTON_RADIUS, fontSize: 12, fontWeight: 600, background: 'transparent', border: `1px solid ${border}`, color: muted, cursor: 'pointer' }}>Decline</button>
          <button onClick={accept} style={{ padding: '6px 16px', borderRadius: BUTTON_RADIUS, fontSize: 12, fontWeight: 700, background: '#ea580c', border: 'none', color: '#fff', cursor: 'pointer', boxShadow: '0 2px 8px rgba(234,88,12,0.35)' }}>Accept</button>
        </div>
      </div>
    </>
  );
};

/* ─── Route layout ── */
const PUBLIC_PATHS = ['/', '/login', '/signup', '/privacy', '/terms'];

const AppRoutes = () => {
  const location = useLocation();
  const { user, loading } = useAuth();
  const isLandingPage = location.pathname === '/';
  /* `!user` used to stand alone here, so on every authenticated route
     (dashboard, skills, any AppLayout page) this was true on the very
     first render — user starts null and only resolves once the async
     auth check completes (see AuthContext) — flashing the public
     Navbar (and its logo) on top of ProtectedRoute's blank loading
     state, then hiding it a moment later once `user` populated and
     this flipped to false. useLocation() resolves synchronously, so
     the PUBLIC_PATHS branch was never the problem; `!user` was
     answering a question auth hadn't finished answering yet. Gating
     it on `!loading` makes this false from the first paint on every
     route that will end up authenticated, instead of true-then-false. */
  const showNav = !isLandingPage && (PUBLIC_PATHS.some(p => location.pathname === p) || (!loading && !user));

  /* ── Scroll to top on every route change — SPAs don't do this on their own ── */
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <>
      {showNav && <Navbar />}
      <Routes>
        {/* Public */}
        <Route path="/"        element={<Landing />} />
        <Route path="/login"   element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/signup"  element={<PublicRoute><Signup /></PublicRoute>} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms"   element={<Terms />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/about"   element={<About />} />
        <Route path="/faq"     element={<Faq />} />

        {/* Voice Room — standalone, no AppLayout (immersive full-screen) */}
        <Route path="/voice-room" element={<ProtectedRoute><VoiceRoom /></ProtectedRoute>} />
        <Route path="/voice-room/:scenarioId" element={<ProtectedRoute><VoiceRoom /></ProtectedRoute>} />
        <Route path="/signin"  element={<Navigate to="/login" replace />} />
        <Route path="/tos"     element={<Navigate to="/terms" replace />} />

        {/* Onboarding */}
        <Route path="/onboarding" element={<OnboardingRoute><Onboarding /></OnboardingRoute>} />

        {/* ── SKILL-SCOPED ROUTES ── */}
        <Route path="/skills" element={
          <ProtectedRoute><AppLayout><Skills /></AppLayout></ProtectedRoute>
        } />
        <Route path="/skills/ai-prompting" element={
          <ProtectedRoute><AppLayout><PromptLanding /></AppLayout></ProtectedRoute>
        } />
        <Route path="/skills/sales-negotiation" element={
          <ProtectedRoute><AppLayout><SalesNegotiationLanding /></AppLayout></ProtectedRoute>
        } />
        <Route path="/skills/copywriting" element={
          <ProtectedRoute><AppLayout><CopywritingLanding /></AppLayout></ProtectedRoute>
        } />
        <Route path="/skills/copywriting/headline-duel/:exerciseId?" element={
          <ProtectedRoute><AppLayout><HeadlineDuel /></AppLayout></ProtectedRoute>
        } />
        <Route path="/skills/copywriting/living-ad/:exerciseId?" element={
          <ProtectedRoute><AppLayout><LivingAd /></AppLayout></ProtectedRoute>
        } />
        <Route path="/skills/copywriting/swipe-file/:exerciseId?" element={
          <ProtectedRoute><AppLayout><SwipeFile /></AppLayout></ProtectedRoute>
        } />
        <Route path="/skills/copywriting/brief/:exerciseId?" element={
          <ProtectedRoute><AppLayout><CopywritingBrief /></AppLayout></ProtectedRoute>
        } />
        <Route path="/skills/copywriting/ab-arena/:exerciseId?" element={
          <ProtectedRoute><AppLayout><ABArena /></AppLayout></ProtectedRoute>
        } />

        {/* ── Precision Storytelling — slugs match features/storytelling/modules.js ── */}
        <Route path="/skills/precision-storytelling" element={
          <ProtectedRoute><AppLayout><StorytellingLanding /></AppLayout></ProtectedRoute>
        } />
        <Route path="/skills/precision-storytelling/origin-cut/:exerciseId?" element={
          <ProtectedRoute><AppLayout><OriginCut /></AppLayout></ProtectedRoute>
        } />
        <Route path="/skills/precision-storytelling/turning-point/:exerciseId?" element={
          <ProtectedRoute><AppLayout><TurningPoint /></AppLayout></ProtectedRoute>
        } />
        <Route path="/skills/precision-storytelling/data-story/:exerciseId?" element={
          <ProtectedRoute><AppLayout><DataStory /></AppLayout></ProtectedRoute>
        } />
        <Route path="/skills/precision-storytelling/pitch-arc/:exerciseId?" element={
          <ProtectedRoute><AppLayout><PitchArc /></AppLayout></ProtectedRoute>
        } />
        <Route path="/skills/precision-storytelling/callback/:exerciseId?" element={
          <ProtectedRoute><AppLayout><Callback /></AppLayout></ProtectedRoute>
        } />
        {/* v2: reps, not "exercise" — keyed by position in the URL (the
            backend resolves lesson_id+position to the real, stable
            rep_id; see Rep.findByLessonAndOrder). */}
        <Route path="/skills/:skillSlug/lessons/:lessonId/reps/:position" element={
          <ProtectedRoute><AppLayout><PromptSandbox /></AppLayout></ProtectedRoute>
        } />

        {/* ── LEGACY REDIRECTS ── */}
        <Route path="/skills/:skillSlug/lessons" element={<LessonSelectRedirect />} />
        <Route path="/lessons" element={<Navigate to="/skills/ai-prompting" replace />} />
        <Route path="/lessons/:lessonId/exercise/:exerciseId" element={<LegacyExerciseRedirect />} />
        {/* Old ai-prompting exercise URLs -> the v2 reps route, same skill/lesson/position values */}
        <Route path="/skills/:skillSlug/lessons/:lessonId/exercise/:exerciseId" element={<LegacyRepRedirect />} />

        {/* Protected — other pages */}
        <Route path="/dashboard" element={
          <ProtectedRoute><AppLayout><Dashboard /></AppLayout></ProtectedRoute>
        } />
        <Route path="/feedback" element={
          <ProtectedRoute><AppLayout><Feedback /></AppLayout></ProtectedRoute>
        } />
        <Route path="/billing" element={
          <ProtectedRoute><AppLayout><Billing /></AppLayout></ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute><AppLayout><Settings /></AppLayout></ProtectedRoute>
        } />

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>

      <CookieBanner />
    </>
  );
};

const LessonSelectRedirect = () => {
  const { skillSlug } = useParams();
  return <Navigate to={`/skills/${skillSlug}`} replace />;
};

const LegacyExerciseRedirect = () => {
  const location = useLocation();
  const newPath = location.pathname.replace('/lessons/', '/skills/ai-prompting/lessons/');
  return <Navigate to={newPath} replace />;
};

// v2: old ai-prompting URLs used /exercise/:exerciseId (a position
// number, same as the new /reps/:position segment) — redirect rather
// than break bookmarked/shared links, same pattern as
// LegacyExerciseRedirect above.
const LegacyRepRedirect = () => {
  const location = useLocation();
  const newPath = location.pathname.replace('/exercise/', '/reps/');
  return <Navigate to={newPath} replace />;
};

const App = () => (
  <AuthProvider>
    <AppRoutes />
  </AuthProvider>
);

export default App;